import React, { useState, useEffect } from 'react';
import { 
  Users, 
  Search, 
  UserPlus, 
  UserCheck, 
  Filter,
  Globe,
  TrendingUp,
  Building,
  MapPin,
  Sparkles,
  RefreshCw,
  Users as UsersIcon,
  User,
  Briefcase,
  Clock,
  CheckCircle
} from 'lucide-react';
import useCommunityStore from '../../store/communityStore';
import useAuthStore from '../../store/authStore';
import followService from '../../services/follow.service';
import { toast } from 'react-hot-toast';
import FollowButton from './FollowButton';

const NetworkTab = () => {
  const [searchQuery, setSearchQuery] = useState('');
  const [isSearching, setIsSearching] = useState(false);
  const [followSuggestions, setFollowSuggestions] = useState([]);
  const [followers, setFollowers] = useState([]);
  const [following, setFollowing] = useState([]);
  const [mutualFollows, setMutualFollows] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [activeView, setActiveView] = useState('discover');
  
  const { 
    searchResults, 
    searchUsers, 
    searchPagination,
    getFollowSuggestions,
    getMutualFollows,
    isLoading: isSearchLoading,
    clearSearchResults
  } = useCommunityStore();
  
  const { user } = useAuthStore();

  useEffect(() => {
    loadNetworkData();
  }, [activeView]);

  const loadNetworkData = async () => {
    setIsLoading(true);
    try {
      switch (activeView) {
        case 'discover':
          await loadSuggestions();
          break;
        case 'followers':
          await loadFollowers();
          break;
        case 'following':
          await loadFollowing();
          break;
      }
    } catch (error) {
      console.error('Failed to load network data:', error);
      toast.error('Failed to load network data');
    } finally {
      setIsLoading(false);
    }
  };

  const loadSuggestions = async () => {
    try {
      const suggestionsResponse = await getFollowSuggestions();
      setFollowSuggestions(suggestionsResponse.data || suggestionsResponse || []);
      
      if (user?.id) {
        const mutualResponse = await getMutualFollows(user.id);
        if (mutualResponse.data?.mutualFollows) {
          setMutualFollows(mutualResponse.data.mutualFollows);
        } else if (mutualResponse.mutualFollows) {
          setMutualFollows(mutualResponse.mutualFollows);
        } else if (Array.isArray(mutualResponse)) {
          setMutualFollows(mutualResponse);
        } else {
          setMutualFollows([]);
        }
      }
    } catch (error) {
      console.error('Failed to load suggestions:', error);
      toast.error('Failed to load suggestions');
    }
  };

  const loadFollowers = async () => {
    try {
      if (user?.id) {
        const response = await followService.getFollowers(user.id);
        setFollowers(response.followers || response || []);
      }
    } catch (error) {
      console.error('Failed to load followers:', error);
      toast.error('Failed to load followers');
    }
  };

  const loadFollowing = async () => {
    try {
      if (user?.id) {
        const response = await followService.getFollowing(user.id);
        setFollowing(response.following || response || []);
      }
    } catch (error) {
      console.error('Failed to load following:', error);
      toast.error('Failed to load following');
    }
  };

  const handleSearch = async (e) => {
    e.preventDefault();
    if (!searchQuery.trim()) return;

    setIsSearching(true);
    try {
      await searchUsers(searchQuery, { limit: 20 });
    } catch (error) {
      console.error('Search error:', error);
      toast.error('Search failed');
    } finally {
      setIsSearching(false);
    }
  };

  const handleFollowSuccess = () => {
    if (activeView === 'discover') {
      loadSuggestions();
    } else {
      loadNetworkData();
    }
  };

  const views = [
    { id: 'discover', label: 'Discover', icon: Sparkles, color: 'primary' },
    { id: 'followers', label: 'Followers', icon: UsersIcon, color: 'blue' },
    { id: 'following', label: 'Following', icon: UserCheck, color: 'green' },
  ];

  const getDisplayUsers = () => {
    if (activeView === 'followers') return followers;
    if (activeView === 'following') return following;
    if (searchQuery) return searchResults.users;
    return followSuggestions;
  };

  const displayUsers = getDisplayUsers();

  return (
    <div className="space-y-3">
      {/* Header with View Tabs - Compact */}
      <div className="bg-white rounded-xl shadow-neomorph p-3 border border-primary-100/30">
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-3">
          <div className="flex items-center">
            <div className="w-1 h-6 bg-gradient-to-b from-primary-400 to-primary-600 rounded-full mr-2" />
            <div>
              <h2 className="text-base font-bold text-gray-800">Network</h2>
              <p className="text-xs text-gray-500">Connect with business professionals</p>
            </div>
          </div>
          
          <button
            onClick={loadNetworkData}
            disabled={isLoading}
            className="px-3 py-1.5 bg-white text-gray-700 text-sm font-medium rounded-lg shadow-neomorph hover:shadow-neomorph-hover transition-all flex items-center disabled:opacity-50"
          >
            <RefreshCw className={`h-3.5 w-3.5 mr-1.5 ${isLoading ? 'animate-spin' : ''}`} />
            Refresh
          </button>
        </div>
        
        {/* View Tabs - Compact */}
        <div className="mt-2 flex space-x-1.5">
          {views.map((view) => {
            const Icon = view.icon;
            const colorClasses = {
              primary: 'from-primary-500 to-primary-600',
              blue: 'from-blue-500 to-blue-600',
              green: 'from-green-500 to-green-600',
            };
            
            return (
              <button
                key={view.id}
                onClick={() => setActiveView(view.id)}
                className={`
                  flex items-center px-3 py-1.5 rounded-lg font-medium text-xs transition-all
                  ${activeView === view.id
                    ? `bg-gradient-to-r ${colorClasses[view.color]} text-white shadow-md`
                    : 'bg-gray-50 text-gray-600 hover:bg-gray-100'
                  }
                `}
              >
                <div className={`p-0.5 rounded-md mr-1.5 ${
                  activeView === view.id ? 'bg-white/20' : 'bg-gray-200'
                }`}>
                  <Icon className={`h-3.5 w-3.5 ${
                    activeView === view.id ? 'text-white' : 'text-gray-500'
                  }`} />
                </div>
                {view.label}
              </button>
            );
          })}
        </div>
      </div>

      {/* Search - Only show in Discover view */}
      {activeView === 'discover' && (
        <div className="bg-white rounded-xl shadow-neomorph p-3 border border-primary-100/30">
          <form onSubmit={handleSearch} className="flex items-center gap-2">
            <div className="relative flex-1">
              <Search className="absolute left-2.5 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
              <input
                type="search"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search by name or business..."
                className="w-full pl-8 pr-8 py-1.5 bg-white rounded-lg shadow-neomorph-inset border border-primary-100/30 focus:outline-none focus:ring-2 focus:ring-primary-500/20 text-sm"
                disabled={isLoading || isSearching}
              />
              {searchQuery && (
                <button
                  type="button"
                  onClick={() => {
                    setSearchQuery('');
                    clearSearchResults();
                  }}
                  className="absolute right-2 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                >
                  ×
                </button>
              )}
            </div>
            
            <button
              type="submit"
              disabled={isLoading || isSearching || !searchQuery.trim()}
              className="px-3 py-1.5 bg-gradient-to-r from-primary-600 to-primary-700 text-white text-sm font-medium rounded-lg shadow-md shadow-primary-500/30 hover:shadow-lg transition-all flex-shrink-0"
            >
              {isSearching ? '...' : 'Search'}
            </button>
          </form>
        </div>
      )}

      {/* Mutual Connections - Compact */}
      {activeView === 'discover' && mutualFollows.length > 0 && !searchQuery && (
        <div className="bg-white rounded-xl shadow-neomorph p-3 border border-primary-100/30">
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center">
              <div className="w-1 h-4 bg-gradient-to-b from-purple-400 to-purple-600 rounded-full mr-1.5" />
              <h3 className="text-sm font-semibold text-gray-800">Mutual Connections</h3>
            </div>
            <span className="px-2 py-0.5 bg-purple-50 text-purple-700 text-[10px] font-medium rounded-md border border-purple-200">
              {mutualFollows.length}
            </span>
          </div>
          
          <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-6 gap-2">
            {mutualFollows.slice(0, 6).map((connection) => (
              <div 
                key={connection.id}
                className="flex flex-col items-center p-2 bg-gray-50 rounded-lg hover:bg-white hover:shadow-neomorph-hover transition-all cursor-pointer"
                onClick={() => window.location.href = `/community/profile/${connection.id}`}
              >
                <div className="h-10 w-10 rounded-lg bg-gradient-to-br from-purple-500 to-purple-600 shadow-md shadow-purple-500/30 flex items-center justify-center mb-1">
                  {connection.avatar ? (
                    <img 
                      src={connection.avatar} 
                      alt={connection.firstName}
                      className="h-10 w-10 rounded-lg object-cover"
                    />
                  ) : (
                    <span className="text-white font-bold text-sm">
                      {connection.firstName?.[0] || '?'}
                    </span>
                  )}
                </div>
                <h4 className="font-medium text-gray-800 text-xs text-center truncate w-full">
                  {connection.firstName} {connection.lastName?.charAt(0)}.
                </h4>
                <p className="text-[10px] text-gray-500 text-center">Mutual</p>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Users Grid - Compact */}
      <div className="bg-white rounded-xl shadow-neomorph p-3 border border-primary-100/30">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center">
            <div className="w-1 h-4 bg-gradient-to-b from-primary-400 to-primary-600 rounded-full mr-2" />
            <h3 className="text-sm font-semibold text-gray-800">
              {searchQuery ? 'Search Results' : 
                activeView === 'discover' ? 'Recommended' :
                activeView === 'followers' ? 'Followers' : 'Following'
              }
            </h3>
          </div>
          
          {displayUsers.length > 0 && (
            <div className="px-2 py-0.5 bg-gray-50 rounded-md">
              <span className="text-xs font-medium text-gray-600">
                {displayUsers.length}
              </span>
            </div>
          )}
        </div>
        
        {isLoading && displayUsers.length === 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2">
            {[1, 2, 3].map((i) => (
              <div key={i} className="bg-gray-50 rounded-lg p-3 animate-pulse">
                <div className="flex items-center space-x-2 mb-2">
                  <div className="h-10 w-10 rounded-lg bg-gradient-to-br from-gray-200 to-gray-300"></div>
                  <div className="space-y-1 flex-1">
                    <div className="h-3 bg-gray-200 rounded w-20"></div>
                    <div className="h-2 bg-gray-200 rounded w-12"></div>
                  </div>
                </div>
                <div className="h-2 bg-gray-200 rounded w-full mb-1"></div>
                <div className="h-2 bg-gray-200 rounded w-3/4 mb-2"></div>
                <div className="h-7 bg-gray-200 rounded w-full"></div>
              </div>
            ))}
          </div>
        ) : displayUsers.length > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2">
            {displayUsers.map((userItem) => (
              <div 
                key={userItem.id} 
                className="bg-gray-50 rounded-lg p-3 hover:bg-white hover:shadow-neomorph-hover transition-all"
              >
                <div className="flex items-start justify-between">
                  <div 
                    className="flex items-center space-x-2 cursor-pointer flex-1 min-w-0"
                    onClick={() => window.location.href = `/community/profile/${userItem.id}`}
                  >
                    <div className="h-10 w-10 rounded-lg bg-gradient-to-br from-primary-500 to-primary-600 shadow-md shadow-primary-500/30 flex items-center justify-center flex-shrink-0">
                      {userItem.avatar ? (
                        <img 
                          src={userItem.avatar} 
                          alt={userItem.firstName}
                          className="h-10 w-10 rounded-lg object-cover"
                        />
                      ) : (
                        <span className="text-white font-bold text-sm">
                          {userItem.firstName?.[0] || '?'}
                        </span>
                      )}
                    </div>
                    <div className="min-w-0 flex-1">
                      <h4 className="font-semibold text-gray-800 text-sm truncate">
                        {userItem.firstName} {userItem.lastName}
                      </h4>
                      {userItem._count && (
                        <div className="flex items-center space-x-1 text-[10px] text-gray-500">
                          <Users className="h-2.5 w-2.5" />
                          <span>{userItem._count.followers || 0} followers</span>
                          <span>•</span>
                          <span>{userItem._count.posts || 0} posts</span>
                        </div>
                      )}
                    </div>
                  </div>
                  
                  {userItem.id !== user?.id && (
                    <div className="flex-shrink-0 ml-2">
                      <FollowButton 
                        userId={userItem.id}
                        size="sm"
                        onFollowChange={handleFollowSuccess}
                      />
                    </div>
                  )}
                </div>
                
                {userItem.bio && (
                  <p className="text-xs text-gray-600 mt-2 line-clamp-1">
                    {userItem.bio}
                  </p>
                )}
                
                {userItem.businessTags && userItem.businessTags.length > 0 && (
                  <div className="flex flex-wrap gap-1 mt-2">
                    {userItem.businessTags.slice(0, 2).map((tag, index) => (
                      <span key={index} className="px-1.5 py-0.5 text-[10px] font-medium bg-primary-50 text-primary-700 rounded-md border border-primary-200">
                        {tag}
                      </span>
                    ))}
                    {userItem.businessTags.length > 2 && (
                      <span className="px-1.5 py-0.5 text-[10px] text-gray-500 bg-gray-100 rounded-md">
                        +{userItem.businessTags.length - 2}
                      </span>
                    )}
                  </div>
                )}
                
                <div className="mt-2 pt-2 border-t border-primary-100/50">
                  <div className="flex items-center justify-between text-[10px]">
                    <div className="flex items-center gap-1">
                      {activeView === 'followers' && (
                        <span className="flex items-center text-green-600 bg-green-50 px-1.5 py-0.5 rounded-md">
                          <UserCheck className="h-2.5 w-2.5 mr-0.5" />
                          Follows you
                        </span>
                      )}
                      {activeView === 'following' && (
                        <span className="flex items-center text-blue-600 bg-blue-50 px-1.5 py-0.5 rounded-md">
                          <UserCheck className="h-2.5 w-2.5 mr-0.5" />
                          You follow
                        </span>
                      )}
                    </div>
                    {userItem.phoneVerified && (
                      <span className="flex items-center text-green-600 bg-green-50 px-1.5 py-0.5 rounded-md">
                        <CheckCircle className="h-2.5 w-2.5 mr-0.5" />
                        Verified
                      </span>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="text-center py-6">
            <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-gray-50 shadow-neomorph-inset mb-2">
              {activeView === 'discover' ? (
                <Sparkles className="h-6 w-6 text-gray-400" />
              ) : activeView === 'followers' ? (
                <UsersIcon className="h-6 w-6 text-gray-400" />
              ) : (
                <UserCheck className="h-6 w-6 text-gray-400" />
              )}
            </div>
            <h3 className="text-sm font-semibold text-gray-800 mb-1">
              {searchQuery 
                ? 'No users found'
                : activeView === 'discover'
                  ? 'No suggestions'
                  : activeView === 'followers'
                    ? 'No followers yet'
                    : 'Not following anyone'
              }
            </h3>
            <p className="text-xs text-gray-500 max-w-xs mx-auto">
              {searchQuery 
                ? 'Try a different search term'
                : activeView === 'discover'
                  ? 'Start connecting with other professionals'
                  : activeView === 'followers'
                    ? 'Share posts to gain followers'
                    : 'Discover and connect with others'
              }
            </p>
            {activeView === 'discover' && (
              <button
                onClick={loadSuggestions}
                className="mt-3 px-4 py-1.5 bg-gradient-to-r from-primary-600 to-primary-700 text-white text-xs font-medium rounded-lg shadow-md shadow-primary-500/30 hover:shadow-lg"
              >
                Refresh
              </button>
            )}
          </div>
        )}
        
        {/* Load More Button for Search Results */}
        {searchQuery && searchPagination && searchPagination.totalPages > searchPagination.page && (
          <div className="text-center mt-3">
            <button
              onClick={() => searchUsers(searchQuery, { page: searchPagination.page + 1 })}
              disabled={isSearchLoading}
              className="px-4 py-1.5 bg-white text-gray-700 text-sm font-medium rounded-lg shadow-neomorph hover:shadow-neomorph-hover transition-all"
            >
              {isSearchLoading ? 'Loading...' : 'Load More'}
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default NetworkTab;